import React, { useState, useEffect } from 'react';
import { useTable, usePagination, useSortBy } from 'react-table';
import ReactPaginate from 'react-paginate';
import { FaSearch } from 'react-icons/fa'; // Import icons
import { allCancelledJobsInfo, allJobsInfo, manualCapture } from '../../../apiManager/admin/JobsManagement.js';
import { Loader } from '../../../components/base/Loader';
import api from '../../../apiManager/api.js';
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { Search } from "lucide-react";
import { fetchAddress } from '../../../lib/utils';

const ServiceAddressCell = ({ coordinates }) => {
    const [address, setAddress] = useState("Loading...");

    useEffect(() => {
        let isMounted = true;
        const getAddress = async () => {
            if (coordinates && coordinates.length === 2) {
                try {
                    const addr = await fetchAddress(coordinates[1], coordinates[0]);
                    if (isMounted) setAddress(addr);
                } catch (e) {
                    if (isMounted) setAddress("Error fetching address");
                }
            } else {
                if (isMounted) setAddress("No location data");
            }
        };
        getAddress();
        return () => { isMounted = false; };
    }, [coordinates]);

    return <div>{address}</div>;
};

function CustomerTable() {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [dateSortBy, setDateSortBy] = useState('Newest First');
    const [jobId, setJobId] = useState(''); //
    const [currentPage, setCurrentPage] = useState(0);
    const [totalCount, setTotalCount] = useState(10);
    const rowsPerPage = 8;

    useEffect(() => {
        (async function () {
            const res = await api.get(`/job/getAllCancelledJobsCount`);
            setTotalCount(res.data.count);
        })();
    }, [])

    useEffect(() => {
        const getJobsData = async () => {
            try {
                setLoading(true);
                const res = await allCancelledJobsInfo(currentPage + 1);
                console.log(res)
                if (!res) setJobs([]);
                else setJobs(res);
            } catch (error) {
                setError(error.message || "An error occurred while fetching data");
            } finally {
                setLoading(false);
            }
        }
        getJobsData();
    }, [jobId, currentPage]);

    const getTimeDifference = (postedTime, requestedTime) => {
        const diffMs = new Date(requestedTime) - new Date(postedTime);
        const diffSeconds = Math.floor(diffMs / 1000); // Convert to seconds

        if (diffSeconds < 60) return `${diffSeconds} seconds`;
        const diffMinutes = Math.floor(diffSeconds / 60);
        if (diffMinutes < 60) return `${diffMinutes} minutes`;
        const diffHours = Math.floor(diffMinutes / 60);
        if (diffHours < 24) return `${diffHours} hours`;
        const diffDays = Math.floor(diffHours / 24);
        if (diffDays < 30) return `${diffDays} days`;
        const diffMonths = Math.floor(diffDays / 30);
        if (diffMonths < 12) return `${diffMonths} months`;

        return `${Math.floor(diffMonths / 12)} years`;
    };


    const columns = React.useMemo(
        () => [
            {
                Header: 'Job Status',
                accessor: 'jobstatus',
                Cell: ({ row }) => (
                    <div>
                        {row.original.jobDetails.jobStatus}
                    </div>
                ),
            },
            {
                Header: 'Homeowner name',
                accessor: 'homeowner.name',
                Cell: ({ row }) => (
                    <div>
                        {row.original.userDetails.firstName} {row.original.userDetails.lastName}
                    </div>
                ),
            },
            {
                Header: 'Homeowner username',
                accessor: 'homeowner.userName',
                Cell: ({ row }) => (
                    <div>
                        {row.original.userDetails.userName}
                    </div>
                ),
            },
            {
                Header: 'Service Address',
                id: 'serviceAddress',
                Cell: ({ row }) => {
                    const coordinates = row.original.jobDetails?.location?.coordinates;
                    return <ServiceAddressCell coordinates={coordinates} />;
                }
            },
            {
                Header: 'Home Address', accessor: 'address',
                Cell: ({ row }) => (
                    <div>
                        {row.original.userDetails.address}
                    </div>
                ),
            },
            {
                Header: 'Phone Number', accessor: 'phone',
                Cell: ({ row }) => (
                    <div>
                        {row.original.userDetails.phone}
                    </div>
                ),
            },
            {
                Header: 'Email', accessor: 'email',
                Cell: ({ row }) => (
                    <div>
                        {row.original.userDetails.email}
                    </div>
                ),
            },
            {
                Header: 'Post Date', accessor: 'PostDate',
                Cell: ({ row }) => (
                    <div>
                        {new Date(row.original.jobDetails.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                        })}
                    </div>
                ),
            },
            {
                Header: 'Authorization Date Time', accessor: 'authorizationDatetime',
                Cell: ({ row }) => {
                    const date = row.original.jobDetails.authorizationDate;
                    return (<div>
                        {date ? new Date(date).toLocaleString("en-US", {
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                        }) : "-"}
                    </div>)
                },
            },
            {
                Header: 'Instructions by homeowner', accessor: 'instructions',
                Cell: ({ row }) => {
                    const serviceDetail = row.original.jobDetails.serviceDetail;
                    if (!serviceDetail) return <div>Null</div>;
                    const serviceInstruction = serviceDetail.serviceInstruction ? serviceDetail.serviceInstruction.join(" ") : "";
                    if (!serviceInstruction) return <div>Null</div>;
                    return (
                        <div>
                            {serviceInstruction}
                        </div>
                    )
                },
            },
            {
                Header: 'Tool by homeowner', accessor: 'toolbyhomeowner',
                Cell: ({ row }) => {
                    const serviceDetail = row.original.jobDetails.serviceDetail;
                    if (!serviceDetail) return <div>Null</div>;
                    const serviceEquipment = serviceDetail.serviceEquipment ? serviceDetail.serviceEquipment.join(",") : "";
                    if (!serviceEquipment) return <div>Null</div>;
                    return (
                        <div>
                            {serviceEquipment}
                        </div>
                    )
                },
            },
            {
                Header: 'House Owner Job Status',
                accessor: 'status',
                Cell: ({ row }) => {
                    const statusArray = ['Pending', 'Completed', 'In-progress', 'Canceled'];
                    const shovelers = row.original.jobDetails.ShovelerInfo;
                    if (!shovelers.length) return (<button>Null</button>);
                    const currentStatus = shovelers[0].houseOwnerAction;
                    console.log(currentStatus, "Status", row.original)
                    const currentIndex = statusArray.indexOf(currentStatus);
                    const nextStatus = statusArray[(currentIndex + 1) % statusArray.length];

                    return (
                        <button
                            className={`px-3 py-1 rounded cursor-pointer bg-zinc-900 text-white`}
                            onClick={() => {
                                const updatedCustomers = [...jobs];
                                updatedCustomers[row.index].status = nextStatus;
                                setJobs(updatedCustomers);
                            }}
                        >
                            {currentStatus}

                        </button>

                    );
                },
            },
            {
                Header: 'Type of Service', accessor: 'service',
                Cell: ({ row }) => {
                    const serviceDetail = row.original.jobDetails.serviceDetail;
                    if (!serviceDetail) return <div>Null</div>;
                    return (
                        <div>
                            {serviceDetail.service}
                        </div>

                    )
                },
            },
            {
                Header: 'Price Offered', accessor: 'price',
                Cell: ({ row }) => (
                    <div>
                        {row.original.jobDetails.paymentInfo.amount / 100}
                    </div>
                ),
            },
            {
                Header: 'Chat Log',
                accessor: 'chat',
                Cell: ({ row }) => {

                    const [chat, setChat] = useState(undefined);

                    const jobId = row.original.jobDetails._id;

                    const fetchChats = async () => {
                        const url = "/job/admin/getchats/" + jobId
                        const res = await api.get(url);
                        const { data } = res.data;
                        if (!data.length) {
                            alert("No chat history !");
                            return;
                        }
                        setChat(data);
                    }

                    return (
                        <div>
                            {chat ? <button onClick={() => setChat(undefined)} className='fixed flex justify-center items-center top-0 left-0 z-50 bg-black bg-opacity-25 border shadow-lg w-full h-full overflow-scroll'>
                                <div className='bg-white rounded-lg text-start w-96 h-60 p-4 overflow-scroll'>
                                    {chat.map(({ message, sender, timestamp }, i) => (
                                        <p><b>{sender}</b>: {message} <span className='text-xs'><br />{new Date(timestamp).toLocaleString()}</span></p>
                                    ))}
                                </div>
                            </button> : ""}
                            <button onClick={() => fetchChats()} className='underline font-bold'>Chat</button>
                        </div>

                    )
                },
            },
        ],
        [jobs]
    );

    const data = React.useMemo(() => {
        return jobs
            .filter((job) =>
                job.userDetails.userName.toLowerCase().includes(searchTerm.toLowerCase())
            )
            .sort((a, b) => {
                // Convert dates to JavaScript Date objects (if not already)
                const dateA = new Date(a.userDetails.dateJoined);
                const dateB = new Date(b.userDetails.dateJoined);

                // Handle invalid date parsing
                if (isNaN(dateA) || isNaN(dateB)) {
                    return 0; // If either date is invalid, keep the order unchanged
                }
                // Sort by "Newest First" or "Oldest First"
                if (dateSortBy === 'Newest First') {
                    return dateB - dateA; // Newest dates first
                } else if (dateSortBy === 'Oldest First') {
                    return dateA - dateB; // Oldest dates first
                } else {
                    return 0; // If dateSortBy is not valid, don't change order
                }
            });
    }, [jobs, searchTerm, dateSortBy]);


    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize },
    } = useTable(
        {
            columns,
            data: data,
            initialState: { pageIndex: 0, pageSize: 8 },
        },
        useSortBy,
        usePagination
    );

    const handlePageClick = ({ selected }) => {
        setCurrentPage(selected);
    };



    if (loading) {
        return <Loader />;
    }

    if (error) {
        return <p className="text-red-500">{error}</p>;
    }

    if (!jobs.length) {
        return <p>No jobs available</p>;
    }

    return (
        <section className="">
            <div className="mt-6 mb-12">
                <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex flex-col">
                        <h2 className="text-2xl font-semibold tracking-tight">Cancelled Jobs</h2>
                        <p className="text-sm text-muted-foreground">Not Anymore Jobs</p>
                    </div>

                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                        <div className="relative w-full sm:w-auto">
                            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                placeholder="Search..."
                                className="pl-10 w-full sm:w-[200px] lg:w-[250px]"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        <Select value={dateSortBy} onValueChange={setDateSortBy}>
                            <SelectTrigger className="w-full sm:w-[180px]">
                                <SelectValue placeholder="Sort by date" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="Newest First">Newest First</SelectItem>
                                <SelectItem value="Oldest First">Oldest First</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {/* Table */}
                <div className='overflow-x-auto'>
                    <table {...getTableProps()} className="w-full mt-10 text-sm font-medium tracking-normal text-gray-400 border-collapse">
                        <thead>
                            {headerGroups.map(headerGroup => (
                                <tr {...headerGroup.getHeaderGroupProps()}>
                                    {headerGroup.headers.map(column => (
                                        <th {...column.getHeaderProps(column.getSortByToggleProps())} className="px-4 py-2 text-left border-b-0">
                                            {column.render('Header')}
                                            {column.canSort && (
                                                <span className={`ml-2 ${column.isSorted ? '' : 'invisible'}`}>
                                                    {column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : ''}
                                                </span>
                                            )}
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody {...getTableBodyProps()} className="border-b-0">
                            {page.map(row => {
                                prepareRow(row);
                                return (
                                    <tr {...row.getRowProps()} className="border-b-0">
                                        {row.cells.map(cell => {
                                            return <td {...cell.getCellProps()} className="px-4 py-2 border-b-0">{cell.render('Cell')}</td>;
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <ReactPaginate
                    className='pagination-container flex justify-end me-32 mt-10 gap-x-5'
                    previousLabel={
                        <span className="flex items-center justify-center px-2 rounded-md border border-gray-300 hover:bg-gray-300 transition duration-200 ease-in-out">
                            &lt;
                        </span>
                    }
                    nextLabel={
                        <span className="flex items-center justify-center px-2 rounded-md border border-gray-300 hover:bg-gray-300 transition duration-200 ease-in-out">
                            &gt;
                        </span>
                    }
                    breakLabel={<span className="mx-2 text-gray-600">...</span>}
                    breakClassName={`break-me`}
                    marginPagesDisplayed={2}
                    pageRangeDisplayed={5}
                    pageCount={Math.ceil(totalCount / rowsPerPage)}
                    forcePage={currentPage}
                    onPageChange={handlePageClick}
                    containerClassName={`flex items-center`}
                    pageLinkClassName={`bg-gray-200 px-2 py-1 rounded-md border border-gray-300 hover:bg-gray-300 transition duration-200 ease-in-out`}
                    activeLinkClassName={`bg-zinc-900 text-white rounded-md`}
                    disabledClassName={`opacity-50 cursor-not-allowed`}
                />

            </div>
        </section>


    );
}

export default CustomerTable;
